
export function nearPos(pos) {
  const angle = Math.random()*Math.PI*2;
  const dist = 100;
  return {
    x: Math.floor(pos.x + dist * Math.cos(angle)),
    y: Math.floor(pos.y + dist * Math.sin(angle))
  }
}

export function nodesToDict(nodes) {
  const ret = {};
  if (!nodes) return ret;
  for (let node of nodes) {
    if (!(node.type in ret)) ret[node.type] = {};
    const rtype = ret[node.type];
    if (!(node.value in rtype)) {
      rtype[node.value] = node;
    } else {
      console.warn("Ignoring duplicate node in graph data: ", rtype[node.value], node);
    }
  }
  return ret;
}

export function edgesToDict(edges) {
  const ret = {};
  if (!edges) return ret;
  for (let e of edges) {
    if (!(e.from_type in ret)) ret[e.from_type] = {};
    const r_ft = ret[e.from_type];
    if (!(e.from_value in r_ft)) r_ft[e.from_value] = {};
    const r_ftv = r_ft[e.from_value];
    if (!(e.to_type in r_ftv)) r_ftv[e.to_type] = {};
    const r_ftv_tt = r_ftv[e.to_type];
    if (!(e.to_value in r_ftv_tt)) {
      r_ftv_tt[e.to_value] = e;
    } else {
      console.warn("Ignoring duplicate edge in graph data: ", r_ftv_tt[e.to_value], e);
    }
  }
  return ret;
}

function computeEdgeLabel(info) {
  // info is a list of {label, ...} objects. need to return a string with comma-separated unique labels, sorted
  if (!info) return '';
  const labels = {};
  for (let obj of info) {
    labels[obj.label] = 1;
  }
  return Object.keys(labels).sort().join(', ');
}

function mergeTimestampListOfIntervals(t1, t2) {
  if (!t1) return t2;
  // only eliminate duplicates
  const ret = [];
  const d = {};
  for (let interval of t1) {
    d[interval[0] + '_' + interval[1]] = 1;
    ret.push(interval);
  }
  for (let interval of t2) {
    if (d[interval[0] + '_' + interval[1]] === 1) continue;
    ret.push(interval);
  }

  return ret.sort((a, b) => b[1] - a[1]);
}

function mergeInfo(info1GroupedByLabel, info2) {
  if (!info1GroupedByLabel) info1GroupedByLabel = [];
  if (!info2) return info1GroupedByLabel;
  
  let info1 = [];
  for (let labelObj of info1GroupedByLabel) {
    for (let contentObj of labelObj.content) {
      const {source, comment, timestamp} = contentObj;
      info1.push({label: labelObj.label, source, comment, timestamp});
    }
  }
  // TODO: store as a tree instead of list
  const t = {};
  const concatenatedInfo = [...info1, ...info2];
  for (let info of concatenatedInfo) {
    if (!(info.label in t)) t[info.label] = {};
    const tl = t[info.label];
    if (!(info.source in tl)) tl[info.source] = {};
    const tls = tl[info.source];
    
    let timestampListOfIntervals;
    if (!info.timestamp) { // no timestamp
      const nowTs = Date.now();
      timestampListOfIntervals = [[nowTs, nowTs]];
    } else if (!isNaN(info.timestamp)) { // single timestamp
      timestampListOfIntervals = [[info.timestamp, info.timestamp]];
    } else if (info.timestamp.length === 2 && !isNaN(info.timestamp[0]) && !isNaN(info.timestamp[1])) {
      timestampListOfIntervals = [info.timestamp];
    } else {
      timestampListOfIntervals = info.timestamp;
    }

    tls[info.comment] = mergeTimestampListOfIntervals(tls[info.comment], timestampListOfIntervals);
  }
  let ret = [];
  const sortedLabels = Object.keys(t).sort();
  for (let label of sortedLabels) {
    const content = [];
    ret.push({label, content});
    const sortedSources = Object.keys(t[label]).sort();
    for (let source of sortedSources) {
      const sortedComments = Object.keys(t[label][source]).sort((c1, c2) => t[label][source][c2][0][1] - t[label][source][c1][0][1]);
      for (let comment of sortedComments) {
        content.push({source, comment, timestamp: t[label][source][comment]});
      }
    }
  }
  return ret;
}

function mergeTrace(t1, t2) {
  const ret = {...t1};
  for (let msId in t2) {
    if (!(msId in t1)) {
      ret[msId] = t2[msId];
      continue;
    } else {
      ret[msId] = {};
      for (let fetcher in t1[msId]) {
        ret[msId][fetcher] = {...t1[msId][fetcher]};
      }
      for (let fetcher in t2[msId]) {
        ret[msId][fetcher] = {...t2[msId][fetcher]};
      }
    }
  }
  return ret;
}

function mergeVisjsProps(t1, t2) {
  return {...t1, ...t2};
}

function mergeNotes(n1, n2) {
  return {...n1, ...n2};
}

export function mergeNodes(node1, node2) {
  const ret = {...node1};
  let modified = false;
  for (let k2 in node2) {
    if (k2 === 'info') {
      ret.info = mergeInfo(node1.info || [], node2.info || []);
      modified = true;
      continue;
    }
    if (k2 === 'trace') {
      ret.trace = mergeTrace(node1.trace || {}, node2.trace || {});
      modified = true;
      continue;
    }
    if (k2 === 'visjsProps') {
      ret.visjsProps = mergeVisjsProps(node1.visjsProps || {}, node2.visjsProps || {});
      modified = true;
      continue;
    }
    if (k2 === 'notes') {
      ret.notes = mergeNotes(node1.notes || {}, node2.notes || {});
      modified = true;
      continue;
    }
    if (!(k2 in ret)) {
      ret[k2] = node2[k2];
      modified = true;
    }
  }
  if (!modified) return node1;
  // console.log("----MERGING---");
  // console.log(node1);
  // console.log(node2);
  // console.log(ret);
  // console.log('-------------');
  return ret
}

export function mergeEdges(edge1, edge2) {
  const ret = {...edge1};
  let modified = false;
  let label = '';
  for (let k2 in edge2) {
    if (k2 === 'info') {
      ret.info = mergeInfo(edge1.info || [], edge2.info || []);
      label = computeEdgeLabel(ret.info);
      modified = true;
      continue;
    }
    if (k2 === 'trace') {
      ret.trace = mergeTrace(edge1.trace || {}, edge2.trace || {});
      modified = true;
      continue;
    }
    if (!(k2 in ret)) {
      ret[k2] = edge2[k2];
      modified = true;
    }
  }
  if (!('visjsProps' in ret)) {
    ret.visjsProps = {label: label};
  }

  if (!modified) return edge1;
  return ret
}

function ensureMicroserviceTracePath(node, microserviceId) {
  if (!node.trace) node.trace = {};
  if (!node.trace[microserviceId]) node.trace[microserviceId] = {};
}

export function deleteFetcherTrace(node, microserviceId, fetcher) {
  if (node.trace?.[microserviceId]?.[fetcher]) delete node.trace[microserviceId][fetcher];
}

export function setFetcherTraceRunning(node, microserviceId, fetcher, timeInterval) {
  ensureMicroserviceTracePath(node, microserviceId);
  node.trace[microserviceId][fetcher] = {
    run: 2,
    info: 'Running...',
    ts: Date.now(),
    timeInterval
  }
}

export function setFetcherTraceError(node, microserviceId, fetcher, timeInterval, errorMsg) {
  ensureMicroserviceTracePath(node, microserviceId);
  let infoMsg = 'Success: False\nError: ' + (errorMsg || 'Unknown error');
  if (node.trace[microserviceId][fetcher]?.run === 2) {
    const durationMs = Date.now() - node.trace[microserviceId][fetcher].ts;
    infoMsg += '\nQuery took: ' + (durationMs / 1000) + ' seconds';
  }
  node.trace[microserviceId][fetcher] = {
    run: -1,
    info: infoMsg,
    ts: Date.now(),
    timeInterval
  }
}

export function setFetcherTraceSuccess(node, microserviceId, fetcher, timeInterval) {
  ensureMicroserviceTracePath(node, microserviceId);
  let infoMsg = 'Success: True';
  if (node.trace[microserviceId][fetcher]?.run === 2) {
    const durationMs = Date.now() - node.trace[microserviceId][fetcher].ts;
    infoMsg += '\nQuery took: ' + (durationMs / 1000) + ' seconds';
  }
  node.trace[microserviceId][fetcher] = {
    run: 1,
    info: infoMsg,
    ts: Date.now(),
    timeInterval
  }
}

export function getAllNodesWithPosition(allNodes, positionsForVisibleNodes) {
  const ret = [];
  const addedNodes = {};
  for (let node of positionsForVisibleNodes) {
    if (!(node.type in addedNodes)) addedNodes[node.type] = {};
    addedNodes[node.type][node.value] = 1;
    ret.push(node);
  }
  for (let node of allNodes) {
    if (node.type in addedNodes && node.value in addedNodes[node.type]) continue;
    ret.push(node);
  }
  return ret;
}

// TODO: make sure node data is immutable (if not, it can impact undo functionality)
